import { useEffect, useRef, useCallback, useState } from 'react'
import { authService } from '../services/authService'

interface UseActivityTrackerOptions {
    inactivityTimeout?: number // มิลลิวินาที (default: 30 นาที)
    warningTimeout?: number // แสดงเตือนก่อนหมดเวลา (default: 5 นาที)
    heartbeatInterval?: number // ส่ง heartbeat ทุกกี่วินาที (default: 10 นาที)
    enabled?: boolean // เปิด/ปิด activity tracking
}

export const useActivityTracker = (options: UseActivityTrackerOptions = {}) => {
    const {
        inactivityTimeout = 30 * 60 * 1000, // 30 นาที
        warningTimeout = 5 * 60 * 1000,     // 5 นาที
        heartbeatInterval = 10 * 60 * 1000,  // 10 นาที
        enabled = true
    } = options

    const lastActivityRef = useRef<number>(Date.now())
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null)
    const logoutTimerRef = useRef<NodeJS.Timeout | null>(null)
    const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Modal state
    const [showSessionWarning, setShowSessionWarning] = useState(false)
    const [timeRemaining, setTimeRemaining] = useState(warningTimeout / 1000)

    const clearInactivityTimers = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current)
            warningTimerRef.current = null
        }
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current)
            logoutTimerRef.current = null
        }
    }, [])

    const clearAllTimers = useCallback(() => {
        clearInactivityTimers()
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current)
            heartbeatTimerRef.current = null
        }
    }, [clearInactivityTimers])

    // Check if user is authenticated (has token)
    const isAuthenticated = useCallback(() => {
        const token = localStorage.getItem('accessToken')
        if (!token) return false

        try {
            // Check if token is expired
            const payload = JSON.parse(atob(token.split('.')[1]))
            const now = Date.now() / 1000
            return payload.exp > now
        } catch {
            return false
        }
    }, [])

    // Logout function
    const logout = useCallback(() => {
        console.log('🚪 Logging out due to session timeout')

        // Clear all auth data
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('deviceId')
        localStorage.removeItem('rememberMe')
        sessionStorage.clear()

        // Show notification
        if (typeof window !== 'undefined' && (window as any).Swal) {
            (window as any).Swal.fire({
                icon: 'warning',
                title: 'เซสชันหมดอายุ',
                text: 'กรุณาเข้าสู่ระบบใหม่',
                confirmButtonText: 'ตกลง',
                allowOutsideClick: false,
                allowEscapeKey: false
            }).then(() => {
                window.location.reload()
            })
        } else {
            // Fallback should never happen since Swal is imported globally
            console.error('SweetAlert not available')
            window.location.href = '/login'
        }
    }, [])

    // Refresh token function
    const refreshToken = useCallback(async () => {
        const refreshTokenValue = localStorage.getItem('refreshToken')
        if (!refreshTokenValue) {
            throw new Error('No refresh token available')
        }

        try {
            const response = await fetch('/api/v1/user-auth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    refreshToken: refreshTokenValue
                })
            })

            if (!response.ok) {
                throw new Error('Token refresh failed')
            }

            const data = await response.json()
            if (data.success && data.data) {
                const { accessToken, refreshToken: newRefreshToken } = data.data

                // Update tokens
                localStorage.setItem('accessToken', accessToken)
                localStorage.setItem('refreshToken', newRefreshToken)

                console.log('✅ Token refreshed successfully')
                return true
            } else {
                throw new Error('Invalid refresh response')
            }
        } catch (error) {
            console.error('❌ Token refresh failed:', error)
            throw error
        }
    }, [])

    // Reset activity timer
    const resetActivityTimer = useCallback(() => {
        if (!enabled || !isAuthenticated()) return

        lastActivityRef.current = Date.now()

        clearInactivityTimers()

        // Set warning timer
        warningTimerRef.current = setTimeout(() => {
            if (isAuthenticated()) {
                setShowSessionWarning(true)
                setTimeRemaining(warningTimeout / 1000)
            }
        }, inactivityTimeout - warningTimeout)

        // Set logout timer
        logoutTimerRef.current = setTimeout(() => {
            if (isAuthenticated()) {
                logout()
            }
        }, inactivityTimeout)
    }, [inactivityTimeout, warningTimeout, logout, enabled, isAuthenticated, clearInactivityTimers])

    // Send heartbeat to server
    const sendHeartbeat = useCallback(async () => {
        if (!enabled || !isAuthenticated()) return

        try {
            const response = await fetch('/api/v1/user-auth/heartbeat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                }
            })

            if (response.ok) {
                const data = await response.json()
                if (data.success && data.data?.accessToken) {
                    // Update token if new one provided
                    localStorage.setItem('accessToken', data.data.accessToken)
                    console.log('💓 Heartbeat successful, token updated')
                } else {
                    console.log('💓 Heartbeat successful')
                }
            } else {
                throw new Error('Heartbeat failed')
            }
        } catch (error) {
            console.warn('⚠️ Heartbeat failed, trying token refresh:', error)

            // If heartbeat fails, try refresh token
            try {
                await refreshToken()
                console.log('✅ Token refreshed after heartbeat failure')
            } catch (refreshError) {
                console.error('❌ Token refresh failed after heartbeat failure:', refreshError)
                // Don't logout immediately - let the main timeout handle it
            }
        }
    }, [enabled, isAuthenticated, refreshToken])

    // Activity event handlers
    const handleActivity = useCallback(() => {
        if (enabled && isAuthenticated()) {
            resetActivityTimer()
        }
    }, [enabled, isAuthenticated, resetActivityTimer])

    // Setup activity tracking
    useEffect(() => {
        if (!enabled || !isAuthenticated()) {
            // Clear all timers when not authenticated or disabled
            clearAllTimers()
            // Reset modal state
            setShowSessionWarning(false)
            setTimeRemaining(warningTimeout / 1000)
            return
        }

        // Activity events to track
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click'
        ]

        // Add event listeners
        events.forEach(event => {
            document.addEventListener(event, handleActivity, true)
        })

        // Setup heartbeat
        heartbeatTimerRef.current = setInterval(sendHeartbeat, heartbeatInterval)

        // Initial setup
        resetActivityTimer()

        return () => {
            // Cleanup
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true)
            })

            clearAllTimers()
        }
    }, [enabled, isAuthenticated, handleActivity, resetActivityTimer, sendHeartbeat, heartbeatInterval, clearAllTimers, warningTimeout])

    // Handle session extension
    const extendSession = useCallback(async () => {
        setShowSessionWarning(false)
        clearInactivityTimers()
        if (enabled && isAuthenticated()) {
            try {
                const refreshed = await authService.refreshToken()
                if (!refreshed.success) {
                    throw new Error('Token refresh failed')
                }
                resetActivityTimer()
                console.log('✅ Session extended successfully')
            } catch (error) {
                console.error('❌ Failed to extend session:', error)
                // If extension fails, logout
                logout()
            }
        }
    }, [enabled, isAuthenticated, resetActivityTimer, logout, clearInactivityTimers])

    // Handle manual logout
    const handleLogout = useCallback(() => {
        setShowSessionWarning(false)
        clearInactivityTimers()
        if (isAuthenticated()) {
            logout()
        }
    }, [logout, isAuthenticated, clearInactivityTimers])

    return {
        lastActivity: lastActivityRef.current,
        resetActivity: handleActivity,
        sendHeartbeat,
        showSessionWarning,
        timeRemaining,
        extendSession,
        handleLogout,
        isAuthenticated: isAuthenticated()
    }
}
